import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, UserPlus, Shield, RefreshCw, CheckCircle, CheckCircle2, Ban,
  Trash2, Edit, Plus, Lock, Key, MoreVertical, Eye, Mail,
  Activity, Clock, Globe, Monitor, AlertTriangle, Send, XCircle,
  UserX, ShieldOff, Phone, Building2, Zap, Settings, AlertCircle
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { BACKOFFICE_MENUS, getMenuLabel } from "@/config/backofficeMenus";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Helper function to extract error message from API responses
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const errorDetail = error.response?.data?.detail;
  if (typeof errorDetail === 'string') return errorDetail;
  if (Array.isArray(errorDetail)) {
    return errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (errorDetail && typeof errorDetail === 'object') {
    return errorDetail.msg || errorDetail.message || fallback;
  }
  return fallback;
};

// Built-in roles (must match backend ROLES)
const BUILT_IN_ROLES = {
  super_admin: { 
    name: "Super Admin", 
    description: "Full access to all features", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: true },
      { key: "customers", read: true, write: true },
      { key: "support_tickets", read: true, write: true },
      { key: "users_roles", read: true, write: true },
      { key: "invoices", read: true, write: true },
      { key: "subscriptions", read: true, write: true },
      { key: "accounts", read: true, write: true },
      { key: "cms", read: true, write: true },
      { key: "marketing", read: true, write: true },
      { key: "analytics", read: true, write: true },
      { key: "availability", read: true, write: true },
      { key: "api_docs", read: true, write: true },
      { key: "settings", read: true, write: true },
      { key: "audit_logs", read: true, write: true },
    ]
  },
  admin: { 
    name: "Admin", 
    description: "Administrative access without system settings", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: true },
      { key: "customers", read: true, write: true },
      { key: "support_tickets", read: true, write: true },
      { key: "users_roles", read: true, write: true },
      { key: "invoices", read: true, write: true },
      { key: "subscriptions", read: true, write: false },
      { key: "accounts", read: true, write: false },
      { key: "cms", read: true, write: true },
      { key: "marketing", read: true, write: true },
      { key: "analytics", read: true, write: true },
      { key: "availability", read: true, write: true },
      { key: "api_docs", read: true, write: true },
      { key: "audit_logs", read: true, write: false },
    ]
  },
  support: { 
    name: "Customer Support", 
    description: "Handle customer tickets and basic support", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: false },
      { key: "customers", read: true, write: false },
      { key: "support_tickets", read: true, write: true },
      { key: "availability", read: true, write: true },
    ]
  },
  finance: { 
    name: "Finance", 
    description: "Access to billing and financial data", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: false },
      { key: "customers", read: true, write: false },
      { key: "invoices", read: true, write: true },
      { key: "subscriptions", read: true, write: true },
      { key: "accounts", read: true, write: true },
      { key: "analytics", read: true, write: false },
    ]
  },
  marketing: { 
    name: "Marketing", 
    description: "Access to CMS and email campaigns", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: false },
      { key: "cms", read: true, write: true },
      { key: "marketing", read: true, write: true },
      { key: "analytics", read: true, write: false },
    ]
  },
  viewer: { 
    name: "Viewer", 
    description: "Read-only access to dashboard", 
    is_system: true,
    permissions: [
      { key: "dashboard", read: true, write: false },
      { key: "customers", read: true, write: false },
      { key: "analytics", read: true, write: false },
      { key: "audit_logs", read: true, write: false },
    ]
  }
};

// Available menu permissions with read/write granularity
const MENU_OPTIONS = [
  { key: "dashboard", label: "Dashboard", description: "View admin dashboard and stats", supportsWrite: false },
  { key: "customers", label: "Customers", description: "Manage customer companies", supportsWrite: true },
  { key: "support_tickets", label: "Support Tickets", description: "Handle support tickets", supportsWrite: true },
  { key: "users_roles", label: "Users & Roles", description: "Manage internal team", supportsWrite: true },
  { key: "invoices", label: "Invoices", description: "View and send invoices", supportsWrite: true },
  { key: "subscriptions", label: "Subscriptions", description: "Manage subscription plans", supportsWrite: true },
  { key: "accounts", label: "Accounts & Billing", description: "View accounts and billing", supportsWrite: true },
  { key: "cms", label: "Content & CMS", description: "Manage website content", supportsWrite: true },
  { key: "marketing", label: "Marketing", description: "Email campaigns and popups", supportsWrite: true },
  { key: "analytics", label: "Analytics", description: "View platform analytics", supportsWrite: false },
  { key: "availability", label: "Staff Availability", description: "Manage working hours", supportsWrite: true },
  { key: "api_docs", label: "API Documentation", description: "View API docs for testing", supportsWrite: false },
  { key: "settings", label: "Settings", description: "Configure platform settings", supportsWrite: true },
  { key: "audit_logs", label: "Audit Logs", description: "View admin activity logs", supportsWrite: false },
];

// Helper to check permission level
const getPermissionLevel = (permissions, key) => {
  if (!permissions || !Array.isArray(permissions)) return 'none';
  // Check for granular permission object
  const perm = permissions.find(p => typeof p === 'object' ? p.key === key : p === key);
  if (!perm) return 'none';
  if (typeof perm === 'object') {
    return perm.write ? 'write' : 'read';
  }
  // Legacy format - string means full access (write)
  return 'write';
};

// Helper to toggle permission
const togglePermission = (permissions, key, level) => {
  const currentPermissions = permissions || [];
  // Remove any existing permission for this key
  const filtered = currentPermissions.filter(p => typeof p === 'object' ? p.key !== key : p !== key);
  
  if (level === 'none') {
    return filtered;
  }
  
  // Add new permission with level
  return [...filtered, { key, read: true, write: level === 'write' }];
};

export default function BackofficeUsers() {
  const { theme, user: currentUser } = useOutletContext();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  // Internal Users
  const [internalUsers, setInternalUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]); // Combined built-in + custom roles
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "support", user_id: "" });
  // Tab in the Add User dialog: "existing" → grant access to a registered customer
  // "invite" → create a brand-new internal staff member with a temp password
  // so we can add Customer Support / Marketing / Finance team members directly.
  const [addUserMode, setAddUserMode] = useState("invite");
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    phone: "",
    department: "",
    role: "support",
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // User Search for Add Internal User
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedSearchUser, setSelectedSearchUser] = useState(null);
  const [internalUserSettings, setInternalUserSettings] = useState({ allowed_internal_domains: [], require_registered_user: true });
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ allowed_internal_domains: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  // Custom Roles
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    permissions: []
  });
  const [savingRole, setSavingRole] = useState(false);
  
  // Built-in Role Viewer
  const [showBuiltInRoleDialog, setShowBuiltInRoleDialog] = useState(false);
  const [viewingBuiltInRole, setViewingBuiltInRole] = useState(null);
  const [editingBuiltInRole, setEditingBuiltInRole] = useState(false);
  const [builtInRoleOverrides, setBuiltInRoleOverrides] = useState({});
  const [savingBuiltInRole, setSavingBuiltInRole] = useState(false);
  const [loadingRoleOverride, setLoadingRoleOverride] = useState(false);
  
  // Fetch role override when viewing a built-in role
  const fetchRoleOverride = async (roleId) => {
    setLoadingRoleOverride(true);
    try {
      const res = await axios.get(`${API}/backoffice/roles/override/${roleId}`, { withCredentials: true });
      if (res.data.override) {
        setBuiltInRoleOverrides(res.data.override);
      } else {
        setBuiltInRoleOverrides({});
      }
    } catch (error) {
      // No override exists, use defaults
      setBuiltInRoleOverrides({});
    } finally {
      setLoadingRoleOverride(false);
    }
  };
  
  // Change Role Dialog
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [changingRoleUser, setChangingRoleUser] = useState(null);
  const [newRoleValue, setNewRoleValue] = useState('support');
  const [savingRoleChange, setSavingRoleChange] = useState(false);

  // Blocked Users
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState("users");

  // Password reset
  const [resettingPassword, setResettingPassword] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);

  // User Details Dialog
  const [showUserDetailsDialog, setShowUserDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('soft'); // 'soft' or 'permanent'
  const [superAdminCount, setSuperAdminCount] = useState(0);

  // Edit User Dialog
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', role: '', department: '', phone: '' });
  const [savingUser, setSavingUser] = useState(false);

  // Send Email Dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Revoke Access Dialog
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokingAccess, setRevokingAccess] = useState(false);

  // User Status
  const [userStatus, setUserStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Role Deletion Confirmation
  const [showRoleDeleteConfirm, setShowRoleDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roleImpact, setRoleImpact] = useState(null);
  const [loadingRoleImpact, setLoadingRoleImpact] = useState(false);
  const [deletingRole, setDeletingRole] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);
const fetchData = useCallback(async () => {
    setLoadingUsers(true);
    setLoadingBlocked(true);
    try {
      const [usersRes, rolesRes, blockedRes, settingsRes] = await Promise.all([
        axios.get(`${API}/internal/users`, { withCredentials: true }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/backoffice/roles`, { withCredentials: true }).catch(() => ({ data: { roles: [], built_in_roles: BUILT_IN_ROLES } })),
        axios.get(`${API}/user-management/blocked-users`, { withCredentials: true }).catch(() => ({ data: { blocked_users: [] } })),
        axios.get(`${API}/backoffice/internal-user-settings`, { withCredentials: true }).catch(() => ({ data: { allowed_internal_domains: [], require_registered_user: true } }))
      ]);
      
      const users = usersRes.data.users || [];
      setInternalUsers(users);
      setRoles(rolesRes.data.roles || []);
      setBlockedUsers(blockedRes.data.blocked_users || []);
      setInternalUserSettings(settingsRes.data);
      
      // Count super admins for protection
      const superAdmins = users.filter(u => u.role === 'super_admin' && u.is_active);
      setSuperAdminCount(superAdmins.length);
      
      // Combine built-in and custom roles for assignment dropdown
      const builtInRolesList = Object.entries(BUILT_IN_ROLES).map(([key, value]) => ({
        role_id: key,
        ...value,
        is_built_in: true
      }));
      const customRolesList = (rolesRes.data.roles || []).map(r => ({
        ...r,
        is_built_in: false
      }));
      setAllRoles([...builtInRolesList, ...customRolesList]);
      
    } catch (error) {
      console.error("Failed to fetch users data:", error);
    } finally {
      setLoadingUsers(false);
      setLoadingBlocked(false);
    }
  }, []);

  // Search registered users for internal user selection
  const searchRegisteredUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await axios.post(`${API}/backoffice/search-registered-users`, { query, limit: 20 }, { withCredentials: true });
      setSearchResults(res.data.users || []);
    } catch (error) {
      console.error("Failed to search users:", error);
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchRegisteredUsers(userSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // Save internal user settings - adds domain to existing list
  const saveInternalUserSettings = async () => {
    setSavingSettings(true);
    try {
      const newDomain = settingsForm.allowed_internal_domains.trim().toLowerCase().replace('@', '');
      if (!newDomain) {
        toast.error("Please enter a domain");
        return;
      }
      
      // Add to existing domains
      const currentDomains = internalUserSettings.allowed_internal_domains || [];
      if (currentDomains.includes(newDomain)) {
        toast.error(`Domain @${newDomain} already exists`);
        return;
      }
      
      const updatedDomains = [...currentDomains, newDomain];
      
      await axios.put(`${API}/backoffice/internal-user-settings`, {
        allowed_internal_domains: updatedDomains
      }, { withCredentials: true });
      
      toast.success(`Domain @${newDomain} added`);
      setSettingsForm({ allowed_internal_domains: '' });
      setInternalUserSettings(prev => ({ ...prev, allowed_internal_domains: updatedDomains }));
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch user activity
  const fetchUserActivity = async (userId) => {
    setLoadingActivity(true);
    try {
      const res = await axios.get(`${API}/activity/admin/audit?user_id=${userId}&limit=50`, { withCredentials: true });
      setUserActivity(res.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch user activity:", error);
      setUserActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Open user details - navigate to full-page view
  const openUserDetails = (user) => {
    navigate(`/backoffice/users/${user.user_id}`);
  };

  // Fetch user status
  const fetchUserStatus = async (userId) => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/internal/users/${userId}/status`, { withCredentials: true });
      setUserStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch user status:", error);
      setUserStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Create Internal User
  const createInternalUser = async () => {
    if (!selectedSearchUser) {
      toast.error("Please select a registered user");
      return;
    }
    setCreatingUser(true);
    try {
      // Grant backoffice access to existing registered user
      const grantRes = await axios.post(`${API}/backoffice/grant-access`, {
        email: selectedSearchUser.email,
        grant: true
      }, { withCredentials: true });
      
      // If role is specified and not the default, update it
      if (userForm.role && userForm.role !== 'support') {
        await axios.put(`${API}/internal/users/${selectedSearchUser.user_id}/role`, {
          role: userForm.role
        }, { withCredentials: true }).catch(() => {});
      }
      
      toast.success(`Backoffice access granted to ${selectedSearchUser.email}`);
      setShowUserDialog(false);
      setUserForm({ email: "", name: "", role: "support", user_id: "" });
      setSelectedSearchUser(null);
      setUserSearchQuery("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant access");
    } finally {
      setCreatingUser(false);
    }
  };

  // Invite a brand-new internal staff member (not a pre-registered customer).
  // Backend generates a temp password, emails the invite, and inserts into
  // the `internal_users` collection with the chosen role.
  const inviteInternalStaff = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error("Please fill in email and name");
      return;
    }
    setCreatingUser(true);
    try {
      const payload = {
        email: inviteForm.email.toLowerCase().trim(),
        name: inviteForm.name.trim(),
        role: inviteForm.role,
        department: inviteForm.department || null,
        phone: inviteForm.phone || null,
      };
      const r = await axios.post(`${API}/internal/users`, payload, { withCredentials: true });
      if (r.data?.existing_customer) {
        toast.success(`${payload.email} is an existing customer — backoffice access granted. They'll use their existing password.`);
      } else {
        const temp = r.data?.user?.temp_password;
        toast.success(temp
          ? `Invite sent to ${payload.email}. Temp password: ${temp}`
          : `Invite sent to ${payload.email}.`
        );
      }
      setShowUserDialog(false);
      setInviteForm({ email: "", name: "", phone: "", department: "", role: "support" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to invite staff member"));
    } finally {
      setCreatingUser(false);
    }
  };

  // Reset Password
  const handleResetPassword = async (userId, sendEmail = false) => {
    setResettingPassword(userId);
    try {
      const res = await axios.post(`${API}/internal/users/${userId}/reset-password`, {
        send_email: sendEmail
      }, { withCredentials: true });
      setTempPassword(res.data.temp_password);
      setShowPasswordDialog(true);
      if (sendEmail) {
        toast.success("Password reset and sent via email");
      } else {
        toast.success("Password reset successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(null);
    }
  };

  // Block/Unblock Internal User with super admin protection
  const handleToggleBlock = async (user) => {
    // Prevent blocking the last super admin
    if (user.role === 'super_admin' && user.is_active && superAdminCount <= 1) {
      toast.error("Cannot block the last super admin. Create another super admin first.");
      return;
    }
    
    try {
      if (user.is_active) {
        await axios.put(`${API}/internal/users/${user.user_id}`, { is_active: false }, { withCredentials: true });
        toast.success("User blocked");
      } else {
        await axios.put(`${API}/internal/users/${user.user_id}`, { is_active: true }, { withCredentials: true });
        toast.success("User unblocked");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  // Delete user with super admin protection
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    // Prevent deleting the last super admin
    if (userToDelete.role === 'super_admin' && superAdminCount <= 1) {
      toast.error("Cannot delete the last super admin. The system requires at least one super admin.");
      setShowDeleteConfirm(false);
      return;
    }
    
    try {
      if (deleteType === 'permanent') {
        await axios.delete(`${API}/internal/users/${userToDelete.user_id}/permanent`, { withCredentials: true });
        toast.success("User permanently deleted");
      } else {
        await axios.delete(`${API}/internal/users/${userToDelete.user_id}`, { withCredentials: true });
        toast.success("User deactivated");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setDeleteType('soft');
    }
  };

  // Change user role
  const changeUserRole = async () => {
    if (!changingRoleUser || !newRoleValue) return;
    
    setSavingRoleChange(true);
    try {
      await axios.put(`${API}/internal/users/${changingRoleUser.user_id}/role`, {
        role: newRoleValue
      }, { withCredentials: true });
      
      toast.success(`Role changed to ${BUILT_IN_ROLES[newRoleValue]?.name || newRoleValue}`);
      setShowChangeRoleDialog(false);
      setChangingRoleUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change role");
    } finally {
      setSavingRoleChange(false);
    }
  };

  // Open Edit User Dialog
  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'support',
      department: user.department || '',
      phone: user.phone || ''
    });
    setShowEditUserDialog(true);
  };

  // Save User Details
  const saveUserDetails = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      await axios.put(`${API}/internal/users/${editingUser.user_id}/details`, editUserForm, { withCredentials: true });
      toast.success("User details updated");
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSavingUser(false);
    }
  };

  // Send Custom Email
  const sendCustomEmail = async () => {
    if (!emailTarget || !emailForm.subject || !emailForm.message) {
      toast.error("Please fill in subject and message");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/internal/users/${emailTarget.user_id}/send-email`, emailForm, { withCredentials: true });
      toast.success(`Email sent to ${emailTarget.email}`);
      setShowEmailDialog(false);
      setEmailTarget(null);
      setEmailForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Revoke User Access
  const handleRevokeAccess = async () => {
    if (!revokeTarget) return;
    setRevokingAccess(true);
    try {
      const res = await axios.post(`${API}/internal/users/${revokeTarget.user_id}/revoke-access`, {}, { withCredentials: true });
      toast.success(`Access revoked: ${res.data.sessions_terminated} sessions terminated, ${res.data.api_keys_revoked} API keys revoked`);
      setShowRevokeDialog(false);
      setRevokeTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke access");
    } finally {
      setRevokingAccess(false);
    }
  };

  // Unblock User
  const handleUnblockUser = async (userId) => {
    try {
      await axios.post(`${API}/user-management/unblock`, { user_id: userId }, { withCredentials: true });
      toast.success("User unblocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  // Save Custom Role
  const saveRole = async () => {
    if (!roleForm.name) {
      toast.error("Role name is required");
      return;
    }
    if (roleForm.permissions.length === 0) {
      toast.error("Select at least one permission");
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        await axios.put(`${API}/backoffice/roles/${editingRole.role_id}`, roleForm, { withCredentials: true });
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/backoffice/roles`, roleForm, { withCredentials: true });
        toast.success("Role created");
      }
      setShowRoleDialog(false);
      setEditingRole(null);
      setRoleForm({ name: "", description: "", permissions: [] });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save role"));
    } finally {
      setSavingRole(false);
    }
  };

  // Delete Custom Role - with impact preview
  const initiateRoleDelete = async (role) => {
    setRoleToDelete(role);
    setLoadingRoleImpact(true);
    setShowRoleDeleteConfirm(true);
    
    try {
      const res = await axios.get(`${API}/backoffice/roles/${role.role_id}/impact`, { withCredentials: true });
      setRoleImpact(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to fetch role impact"));
      setShowRoleDeleteConfirm(false);
      setRoleToDelete(null);
    } finally {
      setLoadingRoleImpact(false);
    }
  };

  const confirmRoleDelete = async () => {
    if (!roleToDelete) return;
    setDeletingRole(true);
    try {
      await axios.delete(`${API}/backoffice/roles/${roleToDelete.role_id}`, { withCredentials: true });
      toast.success("Role deleted successfully");
      setShowRoleDeleteConfirm(false);
      setRoleToDelete(null);
      setRoleImpact(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete role"));
    } finally {
      setDeletingRole(false);
    }
  };

  // Open edit role dialog
  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      permissions: role.permissions || []
    });
    setShowRoleDialog(true);
  };

  // Helper function to get role name from either built-in or custom roles
  const getRoleName = (roleId) => {
    // Check built-in roles first
    if (BUILT_IN_ROLES[roleId]) {
      return BUILT_IN_ROLES[roleId].name;
    }
    // Check custom roles
    const customRole = roles.find(r => r.role_id === roleId);
    if (customRole) {
      return customRole.name;
    }
    // Fallback to the role ID itself (formatted)
    return roleId?.replace('role_', 'Custom Role ') || 'Unknown';
  };

  // Toggle permission in role form (legacy - for simple toggle)
  const toggleSimplePermission = (key) => {
    setRoleForm(prev => {
      const currentPermissions = prev.permissions || [];
      return {
        ...prev,
        permissions: currentPermissions.includes(key)
          ? currentPermissions.filter(p => p !== key)
          : [...currentPermissions, key]
      };
    });
  };

  const getRoleColor = (role) => {
    if (role === "super_admin") return "bg-violet-600";
    if (role === "admin") return "bg-blue-600";
    if (role === "support") return "bg-green-600";
    if (role === "finance") return "bg-amber-600";
    if (role === "marketing") return "bg-pink-600";
    return "bg-gray-600";
  };

  const getRoleBadgeColor = (role, isDarkMode) => {
    if (role === "super_admin") return isDarkMode ? "bg-violet-500/20 text-violet-400" : "bg-violet-100 text-violet-700";
    if (role === "admin") return isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700";
    if (role === "support") return isDarkMode ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700";
    if (role === "finance") return isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700";
    if (role === "marketing") return isDarkMode ? "bg-pink-500/20 text-pink-400" : "bg-pink-100 text-pink-700";
    return isDarkMode ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-700";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Users & Roles</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage internal team members and access roles</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Super Admin Warning */}
      {superAdminCount <= 1 && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 ${isDark ? 'bg-amber-900/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Only {superAdminCount} Super Admin</p>
            <p className={`text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
              Create another super admin for backup access. The last super admin cannot be deleted or blocked.
            </p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className={`flex gap-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        {[
          { id: "users", label: "Team Members", icon: Users },
          { id: "roles", label: "Roles & Permissions", icon: Shield },
          { id: "blocked", label: "Blocked Users", icon: Ban },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-violet-600 text-violet-600'
                : `border-transparent ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Team Members Tab */}
      {activeTab === "users" && (
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Internal Team ({internalUsers.length})</h3>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setSettingsForm({ 
                    allowed_internal_domains: internalUserSettings.allowed_internal_domains?.join(', ') || '' 
                  });
                  setShowSettingsDialog(true);
                }}
              >
                <Settings className="w-4 h-4 mr-1" /> Domain Settings
              </Button>
              <Button size="sm" onClick={() => setShowUserDialog(true)}>
                <UserPlus className="w-4 h-4 mr-1" /> Add User
              </Button>
            </div>
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
            </div>
          ) : internalUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No internal users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {internalUsers.map(user => (
                <div 
                  key={user.user_id} 
                  className={`p-4 rounded-lg border flex items-center justify-between hover:border-violet-500/50 transition-colors cursor-pointer ${
                    isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => openUserDetails(user)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${getRoleColor(user.role)}`}>
                      {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                        {!user.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">Blocked</span>
                        )}
                      </div>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role, isDark)}`}>
                      {getRoleName(user.role)}
                    </span>
                    {/* Status Indicator */}
                    <div className="flex items-center gap-1">
                      {user.is_active ? (
                        <span className="w-2 h-2 rounded-full bg-green-500" title="Active"></span>
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-red-500" title="Blocked"></span>
                      )}
                      {user.access_revoked && (
                        <ShieldOff className="w-3 h-3 text-red-500" title="Access Revoked" />
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openUserDetails(user); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details & Activity
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditUser(user); }}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit User Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { 
                          e.stopPropagation(); 
                          setChangingRoleUser(user);
                          setNewRoleValue(user.role || 'support');
                          setShowChangeRoleDialog(true);
                        }}>
                          <Shield className="w-4 h-4 mr-2" />
                          Change Role
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => { 
                          e.stopPropagation(); 
                          setEmailTarget(user); 
                          setShowEmailDialog(true); 
                        }}>
                          <Send className="w-4 h-4 mr-2" />
                          Send Custom Email
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleResetPassword(user.user_id, false); }}>
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleResetPassword(user.user_id, true); }}>
                          <Mail className="w-4 h-4 mr-2" />
                          Reset & Email Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleToggleBlock(user); }}
                          disabled={user.role === 'super_admin' && user.is_active && superAdminCount <= 1}
                        >
                          {user.is_active ? (
                            <><Ban className="w-4 h-4 mr-2" /> Block User</>
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-2" /> Unblock User</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setRevokeTarget(user);
                            setShowRevokeDialog(true);
                          }}
                          disabled={user.user_id === currentUser?.user_id || (user.role === 'super_admin' && superAdminCount <= 1)}
                          title={user.role === 'super_admin' && superAdminCount <= 1 ? "Cannot revoke last super admin" : ""}
                        >
                          <ShieldOff className="w-4 h-4 mr-2" />
                          Revoke All Access
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setUserToDelete(user);
                            setDeleteType('soft');
                            setShowDeleteConfirm(true);
                          }}
                          disabled={user.role === 'super_admin' && superAdminCount <= 1}
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Deactivate User
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-500"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setUserToDelete(user);
                            setDeleteType('permanent');
                            setShowDeleteConfirm(true);
                          }}
                          disabled={user.role === 'super_admin' && superAdminCount <= 1}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Permanently Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Roles Tab */}
      {activeTab === "roles" && (
        <div className="space-y-6">
          {/* Built-in Roles */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Built-in Roles</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              System roles with predefined permissions. Super admins can view and override permissions.
            </p>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(BUILT_IN_ROLES).map(([roleId, role]) => (
                <div key={roleId} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRoleColor(roleId)}`}>
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{role.name}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{role.description}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={async () => {
                        setViewingBuiltInRole({ roleId, ...role });
                        setShowBuiltInRoleDialog(true);
                        await fetchRoleOverride(roleId);
                      }}
                      title="View permissions"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Show permissions summary */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {role.permissions?.slice(0, 3).map((perm, idx) => (
                      <span key={typeof perm === 'object' ? perm.key : perm} className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>
                        {typeof perm === 'object' ? perm.key : perm}
                      </span>
                    ))}
                    {role.permissions?.length > 3 && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>
                        +{role.permissions.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Custom Roles */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Custom Roles</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Create roles with specific permissions</p>
              </div>
              <Button size="sm" onClick={() => {
                setEditingRole(null);
                setRoleForm({ name: "", description: "", permissions: [] });
                setShowRoleDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-1" /> Create Role
              </Button>
            </div>
            {roles.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No custom roles created</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {roles.map(role => (
                  <div key={role.role_id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{role.name}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{role.description}</p>
                        <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                          {role.permissions?.length || 0} permissions
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditRole(role)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => initiateRoleDelete(role)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Blocked Users Tab */}
      {activeTab === "blocked" && (
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Blocked Users ({blockedUsers.length})</h3>
          {loadingBlocked ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
            </div>
          ) : blockedUsers.length === 0 ? (
            <div className="text-center py-12">
              <Ban className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No blocked users</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blockedUsers.map(user => (
                <div key={user.user_id} className={`p-4 rounded-lg border flex items-center justify-between ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name || user.email}</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.email}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Blocked: {formatDate(user.blocked_at)}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleUnblockUser(user.user_id)}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Add User Dialog — two modes via tabs:
           • Invite new staff (Customer Support / Marketing / Finance …)
             → creates a brand-new internal_users row with a temp password
               and emails the invite. This is the default for hiring.
           • Grant access to existing customer → the legacy search flow for
             turning a registered SeatPilot user into a backoffice admin. */}
      <Dialog open={showUserDialog} onOpenChange={(open) => {
        setShowUserDialog(open);
        if (!open) {
          setUserSearchQuery("");
          setSearchResults([]);
          setSelectedSearchUser(null);
          setUserForm({ email: "", name: "", role: "support", user_id: "" });
          setInviteForm({ email: "", name: "", phone: "", department: "", role: "support" });
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add Internal Staff</DialogTitle>
            <DialogDescription>
              Invite a new staff member directly, or grant backoffice access to an existing SeatPilot customer.
              {internalUserSettings.allowed_internal_domains?.length > 0 && (
                <span className="block mt-1 text-xs text-amber-600">
                  Restricted to: {internalUserSettings.allowed_internal_domains.map(d => `@${d}`).join(', ')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={addUserMode} onValueChange={setAddUserMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full" data-testid="add-user-tabs">
              <TabsTrigger value="invite" data-testid="add-user-tab-invite">Invite new staff</TabsTrigger>
              <TabsTrigger value="existing" data-testid="add-user-tab-existing">Grant access to customer</TabsTrigger>
            </TabsList>

            {/* INVITE NEW STAFF (fresh hire, not a pre-registered customer) */}
            <TabsContent value="invite" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Full name *</label>
                  <Input
                    data-testid="invite-name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Work email *</label>
                  <Input
                    data-testid="invite-email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="jane@seatpilot.co.uk"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Phone</label>
                  <Input
                    data-testid="invite-phone"
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    placeholder="+44 7700 900123"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Department</label>
                  <Input
                    data-testid="invite-department"
                    value={inviteForm.department}
                    onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
                    placeholder="Customer Service / Marketing …"
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Role *</label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <div className={`px-2 py-1 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Built-in Roles</div>
                    {Object.entries(BUILT_IN_ROLES).map(([roleId, role]) => (
                      <SelectItem key={roleId} value={roleId}>
                        <div className="flex flex-col">
                          <span>{role.name}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                    {roles.length > 0 && (
                      <>
                        <div className={`px-2 py-1 mt-2 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Custom Roles</div>
                        {roles.map(role => (
                          <SelectItem key={role.role_id} value={role.role_id}>
                            <div className="flex flex-col">
                              <span>{role.name}</span>
                              <span className="text-xs text-slate-500">{role.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className={`text-xs p-3 rounded-lg ${isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-violet-50 text-violet-900'}`}>
                <p className="font-semibold">What happens next</p>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                  <li>We&apos;ll email the new staff member a welcome link with a temporary password.</li>
                  <li>They&apos;ll be forced to set a fresh password on their first login.</li>
                  <li>You can change their role, department, or revoke access anytime from this page.</li>
                </ul>
              </div>
            </TabsContent>

            {/* GRANT ACCESS TO AN EXISTING REGISTERED CUSTOMER (legacy flow) */}
            <TabsContent value="existing" className="space-y-4 pt-3">
              {/* User Search */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Search Registered Users *
                </label>
                <div className="relative">
                  <Input 
                    placeholder="Search by email or name..."
                    value={userSearchQuery}
                    onChange={(e) => {
                      setUserSearchQuery(e.target.value);
                      setSelectedSearchUser(null);
                    }}
                    className="pr-8"
                  />
                  {searchingUsers && (
                    <RefreshCw className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && !selectedSearchUser && (
                  <div className={`mt-1 border rounded-lg max-h-48 overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                    {searchResults.map(u => (
                      <div
                        key={u.user_id}
                        onClick={() => {
                          setSelectedSearchUser(u);
                          setUserForm({ 
                            email: u.email, 
                            name: u.name || '', 
                            role: userForm.role,
                            user_id: u.user_id 
                          });
                          setUserSearchQuery(u.email);
                          setSearchResults([]);
                        }}
                        className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-violet-50 ${isDark ? 'hover:bg-slate-700 border-slate-600' : 'hover:bg-violet-50 border-gray-100'}`}
                      >
                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {u.name || 'No Name'}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{u.email}</p>
                        {u.company_name && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            <Building2 className="w-3 h-3 inline mr-1" />{u.company_name}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {userSearchQuery.length >= 2 && searchResults.length === 0 && !searchingUsers && !selectedSearchUser && (
                  <p className="text-xs text-amber-600 mt-1">
                    {internalUserSettings.allowed_internal_domains?.length > 0 
                      ? `No users found. Only users with ${internalUserSettings.allowed_internal_domains.map(d => `@${d}`).join(' or ')} emails can be added.`
                      : 'No registered users found. Users must first register on SeatPilot.'
                    }
                  </p>
                )}
              </div>
              
              {/* Selected User Display */}
              {selectedSearchUser && (
                <div className={`p-3 rounded-lg border-2 border-violet-500 ${isDark ? 'bg-slate-700/50' : 'bg-violet-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedSearchUser.name || selectedSearchUser.email}
                      </p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        {selectedSearchUser.email}
                      </p>
                      {selectedSearchUser.company_name && (
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          <Building2 className="w-3 h-3 inline mr-1" />{selectedSearchUser.company_name}
                        </p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedSearchUser(null);
                        setUserSearchQuery("");
                        setUserForm({ email: "", name: "", role: userForm.role, user_id: "" });
                      }}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Role Selection */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Role</label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <div className={`px-2 py-1 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Built-in Roles</div>
                    {Object.entries(BUILT_IN_ROLES).map(([roleId, role]) => (
                      <SelectItem key={roleId} value={roleId}>
                        <div className="flex flex-col">
                          <span>{role.name}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                    {roles.length > 0 && (
                      <>
                        <div className={`px-2 py-1 mt-2 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Custom Roles</div>
                        {roles.map(role => (
                          <SelectItem key={role.role_id} value={role.role_id}>
                            <div className="flex flex-col">
                              <span>{role.name}</span>
                              <span className="text-xs text-slate-500">{role.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={sendPasswordEmail}
                  onCheckedChange={setSendPasswordEmail}
                />
                <label className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Send notification email
                </label>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            {addUserMode === "invite" ? (
              <Button
                data-testid="invite-submit"
                onClick={inviteInternalStaff}
                disabled={creatingUser || !inviteForm.email || !inviteForm.name}
              >
                {creatingUser ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Send invite
              </Button>
            ) : (
              <Button onClick={createInternalUser} disabled={creatingUser || !selectedSearchUser}>
                {creatingUser ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Grant Access
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Internal User Domain Settings</DialogTitle>
            <DialogDescription>
              Restrict which email domains can be added as internal users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Current Domains List */}
            {internalUserSettings.allowed_internal_domains?.length > 0 && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Allowed Domains
                </label>
                <div className="space-y-2">
                  {internalUserSettings.allowed_internal_domains.map((domain, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-2 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-violet-500" />
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>@{domain}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const newDomains = internalUserSettings.allowed_internal_domains.filter((_, i) => i !== idx);
                          try {
                            await axios.put(`${API}/backoffice/internal-user-settings`, {
                              allowed_internal_domains: newDomains
                            }, { withCredentials: true });
                            setInternalUserSettings(prev => ({ ...prev, allowed_internal_domains: newDomains }));
                            toast.success(`Domain @${domain} removed`);
                          } catch (error) {
                            toast.error("Failed to remove domain");
                          }
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add New Domain */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                Add Domain
              </label>
              <div className="flex gap-2">
                <Input 
                  placeholder="e.g., ticketkart.com"
                  value={settingsForm.allowed_internal_domains}
                  onChange={(e) => setSettingsForm({...settingsForm, allowed_internal_domains: e.target.value.replace('@', '')})}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && settingsForm.allowed_internal_domains.trim()) {
                      e.preventDefault();
                      const newDomain = settingsForm.allowed_internal_domains.trim().toLowerCase();
                      if (!internalUserSettings.allowed_internal_domains?.includes(newDomain)) {
                        saveInternalUserSettings();
                      }
                    }
                  }}
                />
                <Button 
                  onClick={saveInternalUserSettings} 
                  disabled={savingSettings || !settingsForm.allowed_internal_domains.trim()}
                >
                  {savingSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                Enter domain without @ symbol and press Enter or click + to add
              </p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-amber-50'}`}>
              <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                <AlertCircle className="w-4 h-4 inline mr-1" />
                When domains are set, user search will ONLY show users from those domains.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Display Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Password</DialogTitle>
            <DialogDescription>Share this password securely with the user</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className={`p-4 rounded-lg text-center font-mono text-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
              {tempPassword}
            </div>
            <p className={`text-sm mt-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              The user must change this password on first login.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              toast.success("Password copied to clipboard");
            }}>
              Copy Password
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRole ? `Edit ${editingRole.is_built_in ? 'Permissions for ' : ''}${editingRole.name}` : 'Create Custom Role'}</DialogTitle>
            <DialogDescription>
              {editingRole?.is_built_in 
                ? 'Configure menu permissions for this built-in role'
                : 'Define a custom role with specific permissions'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
            {!editingRole?.is_built_in && (
              <>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Role Name *</label>
                  <Input 
                    placeholder="e.g., Content Manager"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                  <Textarea 
                    placeholder="Brief description of this role..."
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                    rows={2}
                  />
                </div>
              </>
            )}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Menu Permissions</label>
              <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Select access level for each section. <strong>Read</strong> = View only, <strong>Write</strong> = Full access (view, create, edit, delete)
              </p>
              <div className="space-y-2">
                {MENU_OPTIONS.map(option => {
                  const permLevel = getPermissionLevel(roleForm.permissions, option.key);
                  return (
                    <div 
                      key={option.key}
                      className={`p-3 rounded-lg border ${
                        permLevel !== 'none'
                          ? 'border-violet-500 bg-violet-500/10'
                          : isDark ? 'border-slate-600' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{option.label}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{option.description}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setRoleForm(prev => ({
                              ...prev,
                              permissions: togglePermission(prev.permissions, option.key, 'none')
                            }))}
                            className={`px-2 py-1 text-xs rounded-l ${
                              permLevel === 'none'
                                ? 'bg-gray-500 text-white'
                                : isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            None
                          </button>
                          <button
                            type="button"
                            onClick={() => setRoleForm(prev => ({
                              ...prev,
                              permissions: togglePermission(prev.permissions, option.key, 'read')
                            }))}
                            className={`px-2 py-1 text-xs ${
                              permLevel === 'read'
                                ? 'bg-blue-500 text-white'
                                : isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            Read
                          </button>
                          {option.supportsWrite && (
                            <button
                              type="button"
                              onClick={() => setRoleForm(prev => ({
                                ...prev,
                                permissions: togglePermission(prev.permissions, option.key, 'write')
                              }))}
                              className={`px-2 py-1 text-xs rounded-r ${
                                permLevel === 'write'
                                  ? 'bg-green-500 text-white'
                                  : isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              Write
                            </button>
                          )}
                          {!option.supportsWrite && (
                            <span className="px-2 py-1 text-xs rounded-r bg-gray-200 text-gray-400">Write</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={saveRole} disabled={savingRole}>
              {savingRole ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Built-in Role Viewer/Editor Dialog */}
      <Dialog open={showBuiltInRoleDialog} onOpenChange={(open) => {
        setShowBuiltInRoleDialog(open);
        if (!open) {
          setEditingBuiltInRole(false);
          setBuiltInRoleOverrides({});
        }
      }}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Shield className="w-5 h-5 text-violet-500" />
              {viewingBuiltInRole?.name} - {editingBuiltInRole ? 'Edit Permissions' : 'View Permissions'}
            </DialogTitle>
            <DialogDescription>
              {editingBuiltInRole 
                ? 'Toggle permissions on/off. Changes will be saved as custom overrides.'
                : 'View current permissions for this role. Click Edit to customize.'}
            </DialogDescription>
          </DialogHeader>
          
          {loadingRoleOverride ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          ) : viewingBuiltInRole?.roleId === 'super_admin' ? (
            // Super Admin - Cannot be edited
            <div className="space-y-4">
              <div className={`p-4 rounded-lg border-2 border-violet-500 ${isDark ? 'bg-violet-900/20' : 'bg-violet-50'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-8 h-8 text-violet-500" />
                  <div>
                    <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Super Admin Role</h4>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                      This role has full system access and cannot be modified
                    </p>
                  </div>
                </div>
                <div className={`p-3 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Full access to all menus and features
                  </p>
                  <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ All capabilities enabled
                  </p>
                  <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Can manage all users and roles
                  </p>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'} flex items-center gap-2`}>
                  <Lock className="w-4 h-4" />
                  Super Admin permissions are locked for security. At least one super admin must exist at all times.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Role Description */}
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {viewingBuiltInRole?.description}
                </p>
                {Object.keys(builtInRoleOverrides).length > 0 && !editingBuiltInRole && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    This role has custom overrides applied
                  </p>
                )}
              </div>
              
              {/* Menu Permissions */}
              <div>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Menu Access
                </h4>
                <div className="space-y-2">
                  {BACKOFFICE_MENUS.map(menu => {
                    // Check override first, then default permissions
                    const defaultPermissions = viewingBuiltInRole?.permissions || [];
                    const overridePermissions = builtInRoleOverrides.permissions;
                    const currentPermissions = overridePermissions || defaultPermissions;
                    
                    // Find permission level for this menu
                    const permEntry = currentPermissions.find(p => 
                      typeof p === 'object' ? p.key === menu.key : p === menu.key
                    );
                    
                    // Determine current level: 'none', 'read', or 'write'
                    let currentLevel = 'none';
                    if (permEntry) {
                      if (typeof permEntry === 'object') {
                        currentLevel = permEntry.write ? 'write' : permEntry.read ? 'read' : 'none';
                      } else {
                        // Simple string permission = full access (write)
                        currentLevel = 'write';
                      }
                    }
                    
                    const setPermissionLevel = (level) => {
                      if (!editingBuiltInRole) return;
                      
                      // Remove existing permission for this key
                      const filtered = (builtInRoleOverrides.permissions || [...defaultPermissions])
                        .filter(p => typeof p === 'object' ? p.key !== menu.key : p !== menu.key);
                      
                      let newPerms = [...filtered];
                      if (level !== 'none') {
                        newPerms.push({
                          key: menu.key,
                          read: level === 'read' || level === 'write',
                          write: level === 'write'
                        });
                      }
                      
                      setBuiltInRoleOverrides({
                        ...builtInRoleOverrides,
                        permissions: newPerms
                      });
                    };
                    
                    return (
                      <div 
                        key={menu.key} 
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          {menu.label}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setPermissionLevel('none')}
                            disabled={!editingBuiltInRole}
                            className={`px-3 py-1 text-xs font-medium rounded-l transition-colors ${
                              currentLevel === 'none'
                                ? 'bg-gray-600 text-white'
                                : isDark 
                                  ? 'bg-slate-700 text-slate-400 hover:bg-slate-600' 
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            } ${!editingBuiltInRole ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
                          >
                            None
                          </button>
                          <button
                            onClick={() => setPermissionLevel('read')}
                            disabled={!editingBuiltInRole}
                            className={`px-3 py-1 text-xs font-medium transition-colors ${
                              currentLevel === 'read'
                                ? 'bg-blue-600 text-white'
                                : isDark 
                                  ? 'bg-slate-700 text-slate-400 hover:bg-slate-600' 
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            } ${!editingBuiltInRole ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
                          >
                            Read
                          </button>
                          <button
                            onClick={() => setPermissionLevel('write')}
                            disabled={!editingBuiltInRole}
                            className={`px-3 py-1 text-xs font-medium rounded-r transition-colors ${
                              currentLevel === 'write'
                                ? 'bg-green-600 text-white'
                                : isDark 
                                  ? 'bg-slate-700 text-slate-400 hover:bg-slate-600' 
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            } ${!editingBuiltInRole ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
                          >
                            Write
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Capability Flags */}
              <div>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Capabilities
                </h4>
                <div className="space-y-2">
                  {[
                    { key: 'can_manage_tickets', label: 'Manage Tickets' },
                    { key: 'can_manage_users', label: 'Manage Users' },
                    { key: 'can_manage_billing', label: 'Manage Billing' },
                    { key: 'can_manage_content', label: 'Manage Content' },
                    { key: 'can_view_analytics', label: 'View Analytics' },
                    { key: 'can_manage_settings', label: 'Manage Settings' }
                  ].map(cap => {
                    const defaultValue = viewingBuiltInRole?.[cap.key];
                    const hasCapability = builtInRoleOverrides[cap.key] ?? defaultValue;
                    
                    return (
                      <div 
                        key={cap.key}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          {cap.label}
                        </span>
                        {editingBuiltInRole ? (
                          <button
                            onClick={() => {
                              setBuiltInRoleOverrides({
                                ...builtInRoleOverrides,
                                [cap.key]: !hasCapability
                              });
                            }}
                            className={`w-12 h-6 rounded-full transition-colors relative ${
                              hasCapability ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform shadow ${
                              hasCapability ? 'translate-x-6' : 'translate-x-0.5'
                            }`} />
                          </button>
                        ) : (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            hasCapability 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {hasCapability ? 'Allowed' : 'Denied'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {editingBuiltInRole ? (
              <>
                <Button variant="outline" onClick={() => {
                  setEditingBuiltInRole(false);
                  // Reset to saved overrides
                  fetchRoleOverride(viewingBuiltInRole?.roleId);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    setSavingBuiltInRole(true);
                    try {
                      await axios.post(`${API}/backoffice/roles/override`, {
                        role_id: viewingBuiltInRole?.roleId,
                        ...builtInRoleOverrides
                      }, { withCredentials: true });
                      toast.success('Role permissions saved successfully');
                      setEditingBuiltInRole(false);
                    } catch (error) {
                      toast.error(getErrorMessage(error, 'Failed to save permissions'));
                    } finally {
                      setSavingBuiltInRole(false);
                    }
                  }}
                  disabled={savingBuiltInRole}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingBuiltInRole ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowBuiltInRoleDialog(false)}>
                  Close
                </Button>
                {/* Don't show Edit button for super_admin */}
                {viewingBuiltInRole?.roleId !== 'super_admin' && (
                  <Button onClick={() => {
                    setEditingBuiltInRole(true);
                    // Initialize overrides with current values
                    if (!builtInRoleOverrides.permissions) {
                      setBuiltInRoleOverrides({
                        ...builtInRoleOverrides,
                        permissions: [...(viewingBuiltInRole?.permissions || [])]
                      });
                    }
                  }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Permissions
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showChangeRoleDialog} onOpenChange={setShowChangeRoleDialog}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Shield className="w-5 h-5 text-violet-500" />
              Change Role
            </DialogTitle>
            <DialogDescription>
              Change the role for {changingRoleUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className={`mb-2 block ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Current Role</Label>
              <div className={`p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(changingRoleUser?.role, isDark)}`}>
                  {getRoleName(changingRoleUser?.role)}
                </span>
              </div>
            </div>
            
            <div>
              <Label className={`mb-2 block ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>New Role</Label>
              <select
                value={newRoleValue}
                onChange={(e) => setNewRoleValue(e.target.value)}
                className={`w-full p-2 rounded border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <optgroup label="Built-in Roles">
                  {Object.entries(BUILT_IN_ROLES).map(([key, role]) => (
                    <option key={key} value={key}>{role.name}</option>
                  ))}
                </optgroup>
                {roles.length > 0 && (
                  <optgroup label="Custom Roles">
                    {roles.map(role => (
                      <option key={role.role_id} value={role.role_id}>{role.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            
            {/* Show new role permissions preview */}
            {newRoleValue && (BUILT_IN_ROLES[newRoleValue] || roles.find(r => r.role_id === newRoleValue)) && (
              <div className={`p-3 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Permissions Preview:
                </p>
                <div className="flex flex-wrap gap-1">
                  {(BUILT_IN_ROLES[newRoleValue]?.permissions || roles.find(r => r.role_id === newRoleValue)?.permissions || []).slice(0, 5).map(perm => (
                    <span key={typeof perm === 'string' ? perm : perm.key} className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>
                      {typeof perm === 'string' ? perm : perm.key}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeRoleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={changeUserRole} disabled={savingRoleChange || newRoleValue === changingRoleUser?.role}>
              {savingRoleChange ? "Saving..." : "Change Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {deleteType === 'permanent' ? (
                <><Trash2 className="w-5 h-5 text-red-500" /> Remove Backoffice Access</>
              ) : (
                <><UserX className="w-5 h-5 text-amber-500" /> Deactivate User</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                {deleteType === 'permanent' 
                  ? `Are you sure you want to remove ${userToDelete?.name}'s backoffice access? Their customer profile will be preserved. Use Customers section to delete customer accounts.`
                  : `Are you sure you want to deactivate ${userToDelete?.name}? They will not be able to log in but their data will be preserved.`
                }
              </p>
              {userToDelete?.role === 'super_admin' && superAdminCount <= 1 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This is the last super admin. Deletion is not allowed.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteConfirm(false); setUserToDelete(null); setDeleteType('soft'); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              disabled={userToDelete?.role === 'super_admin' && superAdminCount <= 1}
              className={deleteType === 'permanent' ? "bg-red-500 hover:bg-red-600" : "bg-amber-500 hover:bg-amber-600"}
            >
              {deleteType === 'permanent' ? 'Remove Access' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
            <DialogDescription>Update {editingUser?.name}&apos;s information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Name</label>
              <Input
                value={editUserForm.name}
                onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Email Address</label>
              <Input
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                placeholder="user@company.com"
              />
            </div>
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Role</label>
              <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm({ ...editUserForm, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BUILT_IN_ROLES).map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Shield className="w-3 h-3" />
                        {role.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Department</label>
              <Input
                value={editUserForm.department}
                onChange={(e) => setEditUserForm({ ...editUserForm, department: e.target.value })}
                placeholder="e.g., Engineering, Sales"
              />
            </div>
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Phone</label>
              <Input
                value={editUserForm.phone}
                onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
                placeholder="+44 xxx xxx xxxx"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
            <Button onClick={saveUserDetails} disabled={savingUser}>
              {savingUser && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-500" />
              Send Email to {emailTarget?.name}
            </DialogTitle>
            <DialogDescription>Compose a custom message to send to {emailTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Subject</label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Message</label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Your message..."
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEmailDialog(false); setEmailTarget(null); setEmailForm({ subject: '', message: '' }); }}>
              Cancel
            </Button>
            <Button onClick={sendCustomEmail} disabled={sendingEmail || !emailForm.subject || !emailForm.message}>
              {sendingEmail && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Access Dialog */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Revoke All Access
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will immediately:</p>
              <ul className="list-disc ml-5 space-y-1 text-sm">
                <li>Terminate all active sessions</li>
                <li>Revoke all API keys</li>
                <li>Force the user to log in again</li>
              </ul>
              <p className="font-medium mt-2">User: {revokeTarget?.name} ({revokeTarget?.email})</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowRevokeDialog(false); setRevokeTarget(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRevokeAccess}
              disabled={revokingAccess}
              className="bg-red-500 hover:bg-red-600"
            >
              {revokingAccess && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role Deletion Confirmation Dialog */}
      <AlertDialog open={showRoleDeleteConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowRoleDeleteConfirm(false);
          setRoleToDelete(null);
          setRoleImpact(null);
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Role: {roleToDelete?.name}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {loadingRoleImpact ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-6 h-6 animate-spin text-violet-500" />
                    <span className="ml-2 text-sm">Checking impact...</span>
                  </div>
                ) : roleImpact ? (
                  <>
                    {roleImpact.affected_users_count > 0 ? (
                      <>
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                          <p className={`font-medium ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                            <Ban className="w-4 h-4 inline mr-1" />
                            {roleImpact.affected_users_count} user{roleImpact.affected_users_count !== 1 ? 's' : ''} will lose access:
                          </p>
                        </div>
                        <div className={`max-h-40 overflow-y-auto rounded-lg border ${isDark ? 'border-slate-600 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                          {roleImpact.affected_users.map((u, idx) => (
                            <div 
                              key={u.user_id} 
                              className={`px-3 py-2 flex items-center gap-2 ${idx !== roleImpact.affected_users.length - 1 ? (isDark ? 'border-b border-slate-700' : 'border-b border-gray-200') : ''}`}
                            >
                              <UserX className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {u.name || 'Unnamed User'}
                                </p>
                                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                  {u.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                          These users will be logged out immediately and will need to be reassigned to a new role.
                        </p>
                      </>
                    ) : (
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                        <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                          <CheckCircle className="w-4 h-4 inline mr-1" />
                          No users are currently assigned to this role. Safe to delete.
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingRole}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRoleDelete}
              disabled={deletingRole || loadingRoleImpact}
              className="bg-red-500 hover:bg-red-600"
            >
              {deletingRole && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {roleImpact?.affected_users_count > 0 ? 'Delete & Revoke Access' : 'Delete Role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
